import { IntlShape } from 'react-intl';

import { Item } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { isDefined } from 'common/utils';
import { buildItemUpdateDataFromItem } from 'modules/menu/items/utils';

import { FileMatch, MatchIdentifier } from './types';

const MIN_WIDTH = 1000;
const MAX_WIDTH = 10000;
const MIN_HEIGHT = 563;
const MAX_HEIGHT = 10000;

export const readImageDimensions = (file: File): Promise<[number, number]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const image = new Image();
      image.onload = () => {
        resolve([image.width, image.height]);
      };
      image.onerror = () => {
        reject();
      };
      image.src = reader.result as string;
    };
    reader.onerror = () => {
      reject();
    };
    reader.readAsDataURL(file);
  });
};

export const buildItemUpdatePayload = (item: Item, imageUrl: string) => {
  const sourceConfig = item.source_config
    ? {
        ...item.source_config,
        field_config: { ...item.source_config.field_config, image_url: 'USER_INPUT' as const },
      }
    : null;
  return buildItemUpdateDataFromItem(item, {
    image: imageUrl,
    images: [{ url: imageUrl, blurhash: null }],
    source_config: sourceConfig,
  });
};

export const isImageFileName = (fileName: string) =>
  ['.jpeg', '.jpg', '.png'].includes(fileName.toLocaleLowerCase().substring(fileName.lastIndexOf('.')));

export const normalizeString = (string: string) => string.trim().replace(/(\s|\:|\/)+/g, '_');

export const matchFilesWithItems = (files: File[], items: Item[], matchBy: MatchIdentifier, intl: IntlShape) =>
  Promise.all(
    files.map<Promise<FileMatch>>(async file => {
      const ext = file.name.substring(file.name.lastIndexOf('.'));
      if (!isImageFileName(file.name)) {
        return {
          status: 'failure',
          file,
          note:
            ext === '.zip'
              ? intl.formatMessage({ id: 'menu.bulk-image.error-file-unzip' })
              : intl.formatMessage({ id: 'menu.bulk-image.error-file-type' }),
        };
      }
      try {
        const [width, height] = await readImageDimensions(file);
        if (width < MIN_WIDTH || width > MAX_WIDTH || height < MIN_HEIGHT || height > MAX_HEIGHT) {
          return {
            status: 'failure',
            file,
            note: intl.formatMessage({ id: 'menu.bulk-image.error-image-size' }),
          };
        }
      } catch (error) {
        return {
          status: 'failure',
          file,
          note: intl.formatMessage({ id: 'menu.bulk-image.error-file-reader' }),
        };
      }
      const matchedItems = items.filter(item => {
        const fileName = file.name.replace(ext, '');
        const matcher = item[matchBy];
        return matcher && normalizeString(matcher) === normalizeString(fileName);
      });
      if (matchedItems.length === 0) {
        return {
          status: 'failure',
          file,
          note: intl.formatMessage({ id: 'menu.bulk-image.error-matching' }),
        };
      }
      return {
        status: 'success',
        file,
        matchedItems,
        note: [
          matchedItems.length > 1 ? intl.formatMessage({ id: 'menu.bulk-image.multiple-matches' }) : undefined,
          matchedItems.some(item => !!item.source_config)
            ? intl.formatMessage({ id: 'menu.bulk-image.feed-override' })
            : undefined,
        ]
          .filter(isDefined)
          .join('; '),
      };
    }),
  );
